import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { useBookingModal } from "../context/BookingModalContext";

const links = [
  { to: "/", key: "nav.home" },
  { to: "/services", key: "nav.services" },
  { to: "/packages", key: "nav.packages" },
  { to: "/contact", key: "nav.contact" },
];

const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
  `relative px-1 py-2 text-sm font-semibold tracking-wide transition-colors ${
    isActive ? "text-brand-400" : "text-navy-100 hover:text-white"
  }`;

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { open } = useBookingModal();
  const onPackagesPage = pathname === "/packages";

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const handleBookNow = () => {
    open({ title: t("packages.cta.customTitle") });
  };

  return (
    <nav className="sticky top-0 z-50 bg-navy-950/95 backdrop-blur border-b border-white/10 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <img
            src={`${process.env.PUBLIC_URL}/logo512.png`}
            alt="Shree Matel Tourism logo"
            className="w-10 h-10 rounded-full bg-white object-contain ring-2 ring-brand-500/60"
          />
          <div className="leading-tight">
            <div className="font-serif text-lg font-bold tracking-wide">
              {t("nav.brandName")} <span className="text-brand-400">{t("nav.brandNameHighlight")}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-navy-300">
              {t("nav.brandTagline")}
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={desktopLinkClass}>
              {({ isActive }) => (
                <>
                  {t(l.key)}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-0 right-0 -bottom-[3px] h-[2px] rounded-full bg-gradient-to-r from-brand-600 to-brand-400"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
          <a
            href="tel:+918866077880"
            className="hidden lg:flex items-center gap-2 text-sm font-semibold text-navy-200 hover:text-brand-300 transition-colors"
          >
            <Phone size={15} className="text-brand-400" />
            +91 88660 77880
          </a>
          <LanguageSwitcher />
          {onPackagesPage ? (
            <button
              onClick={handleBookNow}
              className="bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 px-5 py-2.5 rounded-full font-bold text-sm shadow-gold hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              {t("nav.bookNow")}
            </button>
          ) : (
            <Link
              to="/packages"
              className="bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 px-5 py-2.5 rounded-full font-bold text-sm shadow-gold hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              {t("nav.bookNow")}
            </Link>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="md:hidden overflow-hidden bg-navy-950 border-t border-white/10"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg font-semibold text-[15px] transition-colors ${
                      isActive
                        ? "bg-brand-500/15 text-brand-300"
                        : "text-navy-100 hover:bg-white/5"
                    }`
                  }
                >
                  {t(l.key)}
                </NavLink>
              ))}
              <div className="px-3 py-2">
                <LanguageSwitcher />
              </div>
              <div className="flex items-center gap-3 mt-3">
                <a
                  href="tel:+918866077880"
                  className="flex-1 flex items-center justify-center gap-2 border border-white/20 text-white font-semibold py-3 rounded-full text-sm"
                >
                  <Phone size={15} className="text-brand-400" /> {t("nav.callUs")}
                </a>
                {onPackagesPage ? (
                  <button
                    onClick={handleBookNow}
                    className="flex-1 text-center bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 font-bold py-3 rounded-full text-sm shadow-gold"
                  >
                    {t("nav.bookNow")}
                  </button>
                ) : (
                  <Link
                    to="/packages"
                    className="flex-1 text-center bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 font-bold py-3 rounded-full text-sm shadow-gold"
                  >
                    {t("nav.bookNow")}
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
